import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import localizedFormat from 'dayjs/plugin/localizedFormat';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(localizedFormat);

type DateRange = {
    start: Dayjs | null;
    end: Dayjs | null;
};

const safeZonedDate = (value: dayjs.ConfigType, tz?: string) => {
    if (!tz) {
        return dayjs(value);
    }
    try {
        return dayjs.tz(value, tz);
    } catch {
        return dayjs(value);
    }
};

export function toZonedDate(value: dayjs.ConfigType, tz?: string) {
    return safeZonedDate(value, tz);
}

export function getDefaultTimezone() {
    try {
        return dayjs.tz.guess();
    } catch {
        if (typeof Intl !== 'undefined') {
            return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
        }
        return 'UTC';
    }
}

export function normalizeDateRange(
    competitionDate?: string | null,
    competitionEndDate?: string | null,
    tz?: string
): DateRange {
    if (!competitionDate) {
        return { start: null, end: null };
    }
    const start = safeZonedDate(competitionDate, tz).startOf('day');
    const endRaw = competitionEndDate
        ? safeZonedDate(competitionEndDate, tz)
        : start;
    const end = endRaw.isBefore(start) ? start.endOf('day') : endRaw.endOf('day');
    return { start, end };
}

const applyLocale = (value: Dayjs, locale?: string) => (locale ? value.locale(locale) : value);

export function overlapsRange(
    aStart?: Dayjs | null,
    aEnd?: Dayjs | null,
    bStart?: Dayjs | null,
    bEnd?: Dayjs | null
) {
    if (!aStart || !aEnd || !bStart || !bEnd) {
        return false;
    }
    return !(aEnd.isBefore(bStart) || aStart.isAfter(bEnd));
}

type CompetitionDateFields = {
    competition_id?: string | number;
    competition_date?: string | null;
    competition_end_date?: string | null;
    competition_timezone?: string | null;
};

export function groupByMonthSections<T extends CompetitionDateFields>(
    competitions: T[],
    locale?: string,
    defaultTz?: string,
    sortDirection: 'asc' | 'desc' = 'desc'
) {
    const monthMap = new Map<string, { title: string; items: T[] }>();

    competitions.forEach((competition) => {
        const tz = competition.competition_timezone || defaultTz;
        const dateRef =
            competition.competition_end_date || competition.competition_date;
        if (!dateRef) {
            return;
        }
        const date = safeZonedDate(dateRef, tz);
        const key = date.format('YYYY-MM');
        const rawTitle = applyLocale(date, locale).format('MMMM YYYY');
        const title = rawTitle
            ? rawTitle.charAt(0).toUpperCase() + rawTitle.slice(1)
            : rawTitle;
        if (!monthMap.has(key)) {
            monthMap.set(key, { title, items: [competition] });
        } else {
            monthMap.get(key)?.items.push(competition);
        }
    });

    const byEndDesc = (a: T, b: T) => {
        const aRef = a.competition_end_date || a.competition_date || '';
        const bRef = b.competition_end_date || b.competition_date || '';
        const aDate = safeZonedDate(aRef, a.competition_timezone || defaultTz);
        const bDate = safeZonedDate(bRef, b.competition_timezone || defaultTz);
        return bDate.valueOf() - aDate.valueOf();
    };

    return Array.from(monthMap.entries())
        .sort(([aKey], [bKey]) =>
            sortDirection === 'asc'
                ? aKey.localeCompare(bKey)
                : bKey.localeCompare(aKey)
        )
        .map(([, value]) => ({
            title: value.title,
            data:
                sortDirection === 'asc'
                    ? value.items.sort((a, b) => byEndDesc(b, a))
                    : value.items.sort(byEndDesc),
        }));
}

export function formatCompetitionDateRange(
    start?: string | Dayjs | null,
    end?: string | Dayjs | null,
    tz?: string,
    locale?: string
) {
    if (!start) {
        return '';
    }
    const startDate = dayjs.isDayjs(start) ? start : safeZonedDate(start, tz);
    const endDateRaw = end ? (dayjs.isDayjs(end) ? end : safeZonedDate(end, tz)) : startDate;
    const endDate = endDateRaw.isBefore(startDate) ? startDate : endDateRaw;
    const startDay = startDate.startOf('day');
    const endDay = endDate.startOf('day');
    const localizedStart = applyLocale(startDay, locale);
    const localizedEnd = applyLocale(endDay, locale);

    if (startDay.isSame(endDay, 'day')) {
        return localizedStart.format('LL');
    }
    return localizedStart.format('LL');
}
